import {
  ref,
  shallowRef,
  toValue,
  type MaybeRef,
  type Ref,
  type ShallowRef
} from 'vue'
import { useAppLoading } from '@gat-ship/vue-components'
import type {
  AiExtractDataRequestPayload,
  AiExtractDataResponse,
  ExtractionTarget,
  PromptTargetType,
  TargetPromptAiModel
} from '../model/types'
import { mapExtractionTargetToPromptTarget } from '../model/types'
import { useAiApiCalls } from '../model/api'
import { LOADING_IDS } from '../constants'
import { usePromptAssets } from './usePromptAssets'

export interface UseSmartPasteOptions {
  target: MaybeRef<ExtractionTarget>
  promptType?: PromptTargetType
  additionalPromptText?: MaybeRef<string>
  autoReadClipboard?: boolean
  contextParentId?: string
  onExtracted?: (data: AiExtractDataResponse) => void
}

export interface UseSmartPasteReturn {
  isExtracting: Ref<boolean>
  extractedData: ShallowRef<AiExtractDataResponse | undefined>
  isDialogOpen: Ref<boolean>
  openDialog: () => void
  promptText: Ref<string>
  extract: (text?: string) => Promise<AiExtractDataResponse | undefined>
  selectedAssetId: Ref<string | undefined>
  selectedThinkingModel: Ref<TargetPromptAiModel | undefined>
  promptAssets: ReturnType<typeof usePromptAssets>
}

export function useSmartPaste(
  options: UseSmartPasteOptions
): UseSmartPasteReturn {
  const {
    promptType = 'data-extraction',
    autoReadClipboard = true,
    contextParentId,
    onExtracted
  } = options

  const { extractDataRequest } = useAiApiCalls()
  const { setLoadingId, removeLoadingId } = useAppLoading()

  const isExtracting = ref(false)
  const extractedData = shallowRef<AiExtractDataResponse | undefined>(undefined)
  const isDialogOpen = ref(false)
  const promptText = ref('')

  const promptAssets = usePromptAssets({
    target: mapExtractionTargetToPromptTarget(toValue(options.target)),
    promptType
  })

  async function openDialog() {
    isDialogOpen.value = true
    if (autoReadClipboard) {
      try {
        promptText.value = (await navigator.clipboard.readText()) ?? ''
      } catch {
        // Clipboard access denied — user can still type manually
      }
    }
  }

  async function extract(
    text?: string
  ): Promise<AiExtractDataResponse | undefined> {
    const target = toValue(options.target)
    let inputText = text ?? promptText.value
    const additional = toValue(options.additionalPromptText)
    if (additional) {
      inputText += ` \n\n${additional}`
    }

    const payload: AiExtractDataRequestPayload = {
      target,
      text: inputText,
      customPromptAssetId: promptAssets.getCustomPromptAssetId() ?? null,
      aiModelOverride: promptAssets.getAiModelOverride() ?? null,
      contextParentId
    }

    isExtracting.value = true
    setLoadingId(LOADING_IDS.extract)
    try {
      const response = await extractDataRequest(payload)
      if (response?.data) {
        extractedData.value = response.data
        onExtracted?.(response.data)
        isDialogOpen.value = false
        return response.data
      }
      return undefined
    } finally {
      isExtracting.value = false
      removeLoadingId(LOADING_IDS.extract)
    }
  }

  return {
    isExtracting,
    extractedData,
    isDialogOpen,
    openDialog,
    promptText,
    extract,
    selectedAssetId: promptAssets.selectedAssetId,
    selectedThinkingModel: promptAssets.selectedThinkingModel,
    promptAssets
  }
}
